import React from 'react';
import { Table } from "react-bootstrap";
import "./PrescriptionPrint.css";

const RenderLines = ({ text }) => {
    if (!text || text.trim() === "") return null;

    const lines = text
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l !== "");

    if (lines.length === 1) {
        return <span className="single-line">{lines[0]}</span>;
    }

    return (
        <ul className="multi-line">
            {lines.map((line, index) => (
                <li key={index}>{line}</li>
            ))}
        </ul>
    );
};

const PrescriptionForm = ({ prescriptionDetails }) => {
    const {
        user_type,
        patient_type,
        clinic_name,
        doctor_name,
        doctor_profession,
        registration_number,
        doctor_degree,
        doctor_mobile_number,
        doctor_alternate_mobile_number,
        doctor_email_id,
        clinic_address,
        clinic_website,
        patient_name,
        patient_case_number,
        age,
        gender,
        diagnosis_1,
        diagnosis_2,
        diagnosis_3,
        diagnosis_4,
        followUp,
        investigation,
        remedy,
        advice,
        visit_date,
        next_appointment_date,
        next_appointment_time
    } = prescriptionDetails;

    const diagnosisList = [diagnosis_1, diagnosis_2, diagnosis_3, diagnosis_4].filter(Boolean);
    const diagnosisText = diagnosisList.join("\n");

    const formatDate = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const year = date.getFullYear();

        // Function to add "st", "nd", "rd", or "th" to the day
        const getDaySuffix = (day) => {
            if (day >= 11 && day <= 13) return "th"; // Special case for 11th, 12th, 13th
            switch (day % 10) {
                case 1:
                    return "st";
                case 2:
                    return "nd";
                case 3:
                    return "rd";
                default:
                    return "th";
            }
        };

        return `${day}${getDaySuffix(day)} ${month} ${year}`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return "";

        const [hours, minutes] = timeString.split(":").map(Number);
        const date = new Date();
        date.setHours(hours, minutes);

        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        });
    };

    return (
        <>
            <div id="a4-content" className="a4-page">
                {/* Clinic Header */}
                <div className="header-bottom pb-3 mb-4" style={{ backgroundColor: "#ffffff" }}>
                    <div className="d-flex align-items-center justify-content-between prescription-header">
                        {/* Left: Clinic Logo */}
                        <div className="clinic-logo-wrapper text-start">
                            <img
                                src={`${window.location.origin}/clinic-logo.png`}
                                alt="Clinic Logo"
                                className="clinic-logo"
                            />
                        </div>

                        {/* Right: Clinic Details */}
                        <div className="clinic-details text-end">
                            <div id="clinic_name" className="prescription-clinic-name" style={{
                                fontSize: clinic_name?.length > 60 ? "1.2rem" : clinic_name?.length > 40 ? "1.4rem" : "1.8rem",
                            }}>
                                {(() => {
                                    if (!clinic_name) return null;
                                    const words = clinic_name.trim().split(/\s+/);
                                    const fullName = clinic_name.trim();

                                    if (fullName.length <= 30) {
                                        return <div>{fullName}</div>;  // Short name = 1 line
                                    }

                                    // Long name = split to 2 lines at middle word
                                    const halfway = Math.ceil(words.length / 2);
                                    const firstLine = words.slice(0, halfway).join(" ");
                                    const secondLine = words.slice(halfway).join(" ");
                                    return (
                                        <>
                                            <div>{firstLine}</div>
                                            <div>{secondLine}</div>
                                        </>
                                    );
                                })()}
                            </div>
                            <h5><strong>{doctor_name}</strong></h5>
                            <div>{doctor_profession}</div>
                            <div>{doctor_degree}, Reg. No: {registration_number}</div>
                            <div>Email: {doctor_email_id}</div>
                            <div>
                                Mobile Number: {doctor_mobile_number}
                                {doctor_alternate_mobile_number && (<> / {doctor_alternate_mobile_number}</>)}
                            </div>
                            {clinic_website && (
                                <div>
                                    Website: <a href={clinic_website} target="_blank" rel="noopener noreferrer">{clinic_website}</a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Patient Details & Visit Date */}
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <div><strong>Patient Case No:</strong> {patient_case_number}</div>
                        <div><strong>Patient Name:</strong> {patient_name}</div>
                        <div><strong>Age:</strong> {age} yrs</div>
                        <div><strong>Gender:</strong> {gender}</div>
                    </div>
                    <div>
                        <div><strong>Visit Date:</strong> {formatDate(visit_date)}</div>
                    </div>
                </div>

                {/* Add space above the prescription section */}
                <div className="spacer"></div>

                {/* Prescription Details */}
                {diagnosisList.length > 0 || investigation?.trim() !== "" || remedy?.trim() !== "" || advice?.trim() !== "" || followUp?.trim() !== "" &&
                    <div className="pb-3 mb-4">
                        <h5 className="mb-3">Prescription</h5>
                        <Table bordered responsive className='table prescription-table'>
                            <thead>
                                <tr>
                                    <th>Section</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {patient_type === "NEW" ? (
                                    <>
                                        {diagnosisList.length > 0 &&
                                            <tr>
                                                <td>Diagnosis</td>
                                                <td className='text-left'>
                                                    <RenderLines text={diagnosisText} />
                                                </td>
                                            </tr>
                                        }
                                        {investigation && investigation.trim() !== "" &&
                                            <tr>
                                                <td>Investigation</td>
                                                <td className='text-left'>
                                                    <RenderLines text={investigation} />
                                                </td>
                                            </tr>
                                        }
                                        {remedy && remedy.trim() !== "" && (
                                            <tr>
                                                <td>Remedy</td>
                                                <td className='text-left'>{remedy && remedy.trim() !== "" && (
                                                    <>
                                                        {remedy
                                                            .split("\n\n")
                                                            .map((block, blockIndex) => (
                                                                <div key={blockIndex}>
                                                                    {block.split("\n").map((line, lineIndex) => (
                                                                        <div key={lineIndex}>{line}</div>
                                                                    ))}
                                                                    {blockIndex < remedy.split("\n\n").length - 1 && <br />}
                                                                </div>
                                                            ))}
                                                    </>
                                                )}</td>
                                            </tr>
                                        )}
                                        {advice && advice.trim() !== "" &&
                                            <tr>
                                                <td>Advice</td>
                                                <td className='text-left'>
                                                    <RenderLines text={advice} />
                                                </td>
                                            </tr>
                                        }
                                    </>

                                ) : (
                                    <>
                                        {followUp && followUp.trim() !== "" &&
                                            <tr>
                                                <td>Follow Up</td>
                                                <td className='text-left'>
                                                    <RenderLines text={followUp} />
                                                </td>
                                            </tr>
                                        }
                                        {remedy && remedy.trim() !== "" && (
                                            <tr>
                                                <td>Remedy</td>
                                                <td className='text-left'>{remedy && remedy.trim() !== "" && (
                                                    <>
                                                        {remedy
                                                            .split("\n\n")
                                                            .map((block, blockIndex) => (
                                                                <div key={blockIndex}>
                                                                    {block.split("\n").map((line, lineIndex) => (
                                                                        <div key={lineIndex}>{line}</div>
                                                                    ))}
                                                                    {blockIndex < remedy.split("\n\n").length - 1 && <br />}
                                                                </div>
                                                            ))}
                                                    </>
                                                )}</td>
                                            </tr>
                                        )}
                                        {advice && advice.trim() !== "" &&
                                            <tr>
                                                <td>Advice</td>
                                                <td className='text-left'>
                                                    <RenderLines text={advice} />
                                                </td>
                                            </tr>
                                        }
                                    </>
                                )}
                            </tbody>
                        </Table>
                    </div>
                }

                {/* Next Appointment Section */}
                {next_appointment_date !== null && next_appointment_time !== null && (
                    <div className="doctors-notes-section mt-4">
                        <h5 className="notes-title mb-3">Next Appointment</h5>

                        <div>
                            <div>Date: {formatDate(next_appointment_date)}</div>
                            <div>Time: {formatTime(next_appointment_time)}</div>
                        </div>
                    </div>
                )}

                {/* Signature Section */}
                <div className="signature-container mb-3">
                    <>
                        <p className="doctor-name-placeholder">{doctor_name}</p>
                        <div className="signature-box no-line no-padding">
                            <span className="signature-text">Signature</span>
                        </div>
                    </>
                </div>

                {/* Footer Section */}
                <footer className="clinic-footer">
                    {/* Clinic Address */}
                    <div className="clinic-address">
                        {clinic_address.split("\n").map((line, index) => (
                            <p key={index} style={{ margin: 0 }}>{line}</p>
                        ))}
                    </div>

                    {/* Disclaimer included here */}
                    <div className="disclaimer-section mt-2">
                        <p className="software-credit">Generated by VisionThink Tech - Clinic Management Software</p>
                    </div>
                </footer>
            </div>
        </>
    )
}

export default PrescriptionForm;